import type { IntlShape } from 'react-intl';
import { formatTranslation } from 'intl';
import { DocumentationTranslationKeys } from 'intl/keys';
import type { TranslationKey } from 'intl/types/translationKey';
import type { TranslatedString } from 'intl/types/translatedString';
import type { LevelMapWarning } from 'shared/domain/documentation/levelMap';
import {
  findMapUnsuitableReason,
  formatMapFileFormat,
  MapUnsuitableReason,
  type MapUnsuitableFile,
} from 'shared/domain/documentation/mapUnsuitableReason';
import { levelMapWarningLabelKey } from './levelMapWarningLabelKeys';

const MAP_UNSUITABLE_REASON_KEYS: Record<MapUnsuitableReason, TranslationKey> = {
  [MapUnsuitableReason.unsupportedFormat]: DocumentationTranslationKeys.mapReasonUnsupportedFormat,
  [MapUnsuitableReason.multiPagePdf]: DocumentationTranslationKeys.mapReasonMultiPagePdf,
};

/** Why a file cannot be a map, as a sentence, e.g. "The XLSX format is not supported as a map." (PT-7117). */
export function formatMapUnsuitableReason(
  intl: IntlShape,
  reason: MapUnsuitableReason,
  extension: string | undefined
): TranslatedString {
  return formatTranslation(intl, MAP_UNSUITABLE_REASON_KEYS[reason], { format: formatMapFileFormat(extension) });
}

/**
 * What the user is told about a map that is not displayed because its version
 * cannot be a map: why, and of which sublocations, e.g. "The current version
 * cannot be a map. The XLSX format is not supported as a map. The map of
 * sublocation Building A · Ground floor is not displayed." (PT-7117 mockup).
 * `undefined` when the file does not tell why or the sublocation is unknown.
 */
export function formatMapNotDisplayed(
  intl: IntlShape,
  version: MapUnsuitableFile,
  location: string | undefined
): TranslatedString | undefined {
  const reason = findMapUnsuitableReason(version);
  if (!reason || !location) {
    return undefined;
  }
  return formatTranslation(intl, DocumentationTranslationKeys.mapCurrentVersionNotSuitable, {
    reason: formatMapUnsuitableReason(intl, reason, version.extension),
    location,
  });
}

/**
 * The map warning as the user is told it: why the map is not displayed when
 * the unsuitable version's file says so, otherwise the warning's own label.
 */
export function formatLevelMapWarning(
  intl: IntlShape,
  warning: LevelMapWarning,
  unsuitableFile: MapUnsuitableFile | undefined,
  location: string | undefined
): TranslatedString {
  return (
    (unsuitableFile && formatMapNotDisplayed(intl, unsuitableFile, location)) ||
    formatTranslation(intl, levelMapWarningLabelKey(warning))
  );
}
